import {KeyboardEvent, useState} from "react";
import {useFieldArray, useFormContext} from "react-hook-form";
import {Plus, X} from "lucide-react";
import {CharacterFormInput} from "@/characters/admin/form/CharacterFormSchema.ts";
import {Badge} from "@/components/ui/badge.tsx";
import {Button} from "@/components/ui/button.tsx";
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from "@/components/ui/select.tsx";
import {cn} from "@/lib/utils.ts";

type TagArrayName = "skills" | "languages" | "classes" |
    "damageResistances" | "damageImmunitys" | "damageVulnerabilitys" | "conditionImmunitys";

export default function TagArrayInputField({name, title, selectValues, placeholder, className}: {
    name: TagArrayName;
    title: string;
    selectValues?: readonly string[];
    placeholder?: string;
    className?: string;
}) {
    const {control} = useFormContext<CharacterFormInput>();
    const tags = useFieldArray({control, name});
    const [draft, setDraft] = useState("");
    const values = tags.fields.map(tag => tag.value);

    const add = (value: string) => {
        const normalized = value.trim();
        if (!normalized || values.some(existing => existing.toLocaleLowerCase("de-DE") === normalized.toLocaleLowerCase("de-DE"))) return;
        tags.append({value: normalized});
        setDraft("");
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key !== "Enter" && event.key !== ",") return;
        event.preventDefault();
        add(draft);
    };

    return <section className={cn("space-y-2", className)}>
        <label className="text-sm font-medium" htmlFor={`${name}-tag-input`}>{title}</label>
        <div className="flex min-h-9 flex-wrap items-center gap-1 rounded-md border border-input bg-surface px-2 py-1 shadow-inner shadow-black/10 transition-[border-color,box-shadow] focus-within:ring-3 focus-within:ring-ring/30">
            {tags.fields.map((tag, index) => (
                <Badge key={tag.id} variant="secondary" className="gap-1 pl-2 pr-1 text-sm rounded-sm">
                    {tag.value}
                    <button type="button"
                            className="rounded-full text-muted-foreground hover:bg-destructive/15 hover:text-destructive focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                            onClick={() => tags.remove(index)}
                            aria-label={`${tag.value} entfernen`}>
                        <X className="size-4"/>
                    </button>
                </Badge>
            ))}
            {selectValues ? (
                <Select value="" onValueChange={add}>
                <SelectTrigger id={`${name}-tag-input`} size="sm"
                               className="h-7 min-w-40 grow border-0 bg-transparent px-1 py-0 shadow-none focus-visible:ring-0">
                    <SelectValue placeholder={placeholder ?? `${title} hinzufügen`}/>
                </SelectTrigger>
                <SelectContent>
                    {selectValues.map(value => (
                        <SelectItem key={value} value={value} disabled={values.includes(value)}>{value}</SelectItem>
                    ))}
                </SelectContent>
            </Select>
            ) : <>
                <input id={`${name}-tag-input`}
                       value={draft}
                       placeholder={placeholder ?? "Eintrag hinzufügen"}
                       className="h-7 min-w-32 grow bg-transparent px-1 text-sm outline-none placeholder:text-muted-foreground"
                       onChange={event => setDraft(event.target.value)}
                       onKeyDown={handleKeyDown}/>
                <Button type="button" variant="ghost" size="icon-sm" className="size-7" disabled={!draft.trim()}
                        onClick={() => add(draft)} title={`${title} hinzufügen`}>
                    <Plus className="size-3.5"/>
                </Button>
            </>}
        </div>
    </section>;
}
